import type { ContactFormStep, LoginMethod } from './constants';
import type { User } from './types';
import type { ContactFormModalProps } from '~/components/ContactFormModal';
import type { FormMeta } from 'vee-validate';

import { CONTACT_FORM_STEPS, FORM_SUBMISSION_ACTIONS } from './constants';
import { useAuthService } from './useAuthService';
import { useFormSubmissionStrategy } from './useFormSubmissionStrategy';
import { useLeadService } from './useLeadService';

export interface ContactFormValues {
	name: string;
	phone: string;
	email?: string;
	userMessage?: string;
}

export interface ContactFormModalState {
	// State refs
	currentStep: Ref<ContactFormStep>;
	errorMessage: Ref<string | null>;
	isSubmittingOtpForm: Ref<boolean>;
	isSubmittingForm: Ref<boolean>;
	isOTPValid: Ref<boolean | null>;

	// Props
	props: ContactFormModalProps;

	// Computed refs
	isPropertyContext: ComputedRef<boolean>;
	isUserLoggedIn: ComputedRef<boolean>;
	user: ComputedRef<User | null>;
	initialUserMessage: ComputedRef<string>;

	// Form field refs
	name: ComputedRef<string>;
	email: ComputedRef<string>;
	phone: ComputedRef<string>;
	userMessage: ComputedRef<string>;
	meta: ComputedRef<FormMeta<ContactFormValues>>;

	// Form methods
	validate: () => Promise<{ valid: boolean }>;
	setFormSubmittedSuccessfully: (value: boolean) => void;
	fetchUserSession: () => Promise<void>;
	getAdCampaign: () => Record<string, string>;
	handleSubmit: (
		onSubmit: (values: ContactFormValues, ctx?: unknown) => Promise<void>
	) => (e?: Event) => Promise<Promise<void>>;
}

export function useContactFormActions(modalState: ContactFormModalState) {
	const gtm = useGtm();
	const authService = useAuthService();
	const leadService = useLeadService();
	const { getSubmissionAction } = useFormSubmissionStrategy();

	function trackGTMFormSubmit() {
		if (!gtm) return;

		gtm.trackEvent({
			event: 'form_submit',
			action: 'form_submit',
			label: modalState.isPropertyContext.value
				? 'property_contact_form'
				: 'condo_contact_form',
			form_id: modalState.isPropertyContext.value
				? 'property-price-contact-form'
				: 'contact-form-section',
			form_name: modalState.name.value,
			form_phone: modalState.phone.value,
			form_email: modalState.email.value,
			form_message: modalState.userMessage.value,
			lead_created: true,
			form_context: modalState.isPropertyContext.value ? 'property' : 'condo',
			property_id: modalState.props.propertyCommercialId || null,
			property_price: modalState.props.askingPrice || null,
			currency: modalState.props.askingPrice ? 'BRL' : null
		});
	}

	function goToContactFormStep() {
		modalState.currentStep.value = CONTACT_FORM_STEPS.CONTACT_FORM;
		modalState.setFormSubmittedSuccessfully(false);

		nextTick(async () => {
			await modalState.validate();
		});
	}

	function goToSuccessMessage() {
		modalState.currentStep.value = CONTACT_FORM_STEPS.SUCCESS_MESSAGE;
		modalState.setFormSubmittedSuccessfully(true);
		trackGTMFormSubmit();
	}

	function goToErrorMessage(error?: string) {
		modalState.currentStep.value = CONTACT_FORM_STEPS.ERROR_MESSAGE;
		if (error) {
			modalState.errorMessage.value = error;
		}
	}

	async function goToOtpFormStep(shouldSendCode = false) {
		modalState.currentStep.value = CONTACT_FORM_STEPS.OTP_FORM;

		if (shouldSendCode && modalState.phone.value) {
			await authService.sendOtpCode(modalState.phone.value);
		}
	}

	async function submitLead(values: ContactFormValues): Promise<boolean> {
		const adCampaign = modalState.getAdCampaign();
		const leadData = {
			name: values.name || (modalState.user.value?.name ?? '') || '',
			phone: values.phone,
			email: values.email || (modalState.user.value?.email ?? undefined),
			message:
				values.userMessage?.trim() || modalState.initialUserMessage.value,
			adCampaign
		};

		const result = modalState.isPropertyContext.value
			? await leadService.createPropertyLead({
					...leadData,
					propertyId: modalState.props.propertyId!
				})
			: await leadService.createCondoLead({
					...leadData,
					condoId: modalState.props.condoId!,
					condoName: modalState.props.condoName ?? '',
					companyDistribution: modalState.props.companyDistribution!
				});

		if (!result.success) {
			goToErrorMessage(result.error);
			return false;
		}

		goToSuccessMessage();
		return true;
	}

	const handleFormSubmit = modalState.handleSubmit(
		async (values: ContactFormValues) => {
			try {
				await submitLead(values);
			} catch (error) {
				const errorConverted = error as Error;
				goToErrorMessage(errorConverted?.message);
				return Promise.resolve();
			}
		}
	);

	async function completePhoneRegistration(): Promise<boolean> {
		const result = await authService.completePhoneRegistration(
			modalState.name.value,
			modalState.email.value || undefined
		);

		if (!result.success) {
			goToErrorMessage(result.error);
			return false;
		}

		await modalState.fetchUserSession();
		return true;
	}

	async function completeSocialRegistration(): Promise<boolean> {
		const result = await authService.completeSocialRegistration(
			modalState.phone.value
		);

		if (!result.success) {
			goToErrorMessage(result.error);
			return false;
		}

		await modalState.fetchUserSession();

		const otpResult = await authService.sendOtpCode(modalState.phone.value);
		if (!otpResult.success) {
			goToErrorMessage(otpResult.error);
			return false;
		}

		await goToOtpFormStep();
		return true;
	}

	async function startOtpFlow(): Promise<boolean> {
		const result = await authService.sendOtpCode(modalState.phone.value);

		if (!result.success) {
			goToErrorMessage(result.error);
			return false;
		}

		await goToOtpFormStep();
		return true;
	}

	async function sendForm() {
		try {
			modalState.isSubmittingForm.value = true;

			await modalState.validate();
			if (!modalState.meta.value.valid) {
				return;
			}

			const action = getSubmissionAction({
				isLoggedIn: modalState.isUserLoggedIn.value,
				loginMethod: modalState.user.value?.login_method as
					| LoginMethod
					| undefined,
				hasCompletedRegistration:
					modalState.user.value?.hasCompletedRegistration ?? false
			});

			let success = false;

			switch (action) {
				case FORM_SUBMISSION_ACTIONS.SUBMIT:
					await handleFormSubmit();
					success = true;
					break;

				case FORM_SUBMISSION_ACTIONS.COMPLETE_PHONE:
					success = await completePhoneRegistration();
					if (success) {
						await handleFormSubmit();
					}
					break;

				case FORM_SUBMISSION_ACTIONS.COMPLETE_SOCIAL:
					success = await completeSocialRegistration();
					break;

				case FORM_SUBMISSION_ACTIONS.OTP:
					success = await startOtpFlow();
					break;
			}
		} catch (error) {
			throw error;
		} finally {
			modalState.isSubmittingForm.value = false;
		}
	}

	async function submitOtp(otp: string, isSocialUser: boolean) {
		if (modalState.isSubmittingOtpForm.value) return;

		try {
			modalState.isSubmittingOtpForm.value = true;

			const result = isSocialUser
				? await authService.validateSocialOtp(otp)
				: await authService.validatePhoneOtp({
						phoneNumber: modalState.phone.value,
						otp,
						email: modalState.email.value || undefined,
						name: modalState.name.value || undefined
					});

			if (!result.success) {
				modalState.isOTPValid.value = false;
				return;
			}

			modalState.isOTPValid.value = true;

			await modalState.fetchUserSession();
			(await handleFormSubmit()) ?? Promise.resolve();
		} catch (error) {
			const errorConverted = error as Error;
			goToErrorMessage(errorConverted?.message);
		} finally {
			modalState.isSubmittingOtpForm.value = false;
		}
	}

	function handleOtpFormValidation(otp: string) {
		const isSocialUser =
			modalState.isUserLoggedIn.value &&
			(modalState.user.value?.login_method === 'google' ||
				modalState.user.value?.login_method === 'facebook');

		submitOtp(otp, isSocialUser);
	}

	async function resendOtp(): Promise<void> {
		// Reset OTP validation state before resending
		modalState.isOTPValid.value = null;
		await authService.sendOtpCode(modalState.phone.value);
	}

	async function submitExternalForm() {
		try {
			await modalState.validate();
			if (!modalState.meta.value.valid) {
				return { success: false, step: CONTACT_FORM_STEPS.CONTACT_FORM };
			}

			const values = {
				name: modalState.name.value,
				phone: modalState.phone.value,
				email: modalState.email.value,
				userMessage: modalState.userMessage.value
			};

			const success = await submitLead(values);

			return {
				success,
				step: success
					? CONTACT_FORM_STEPS.SUCCESS_MESSAGE
					: CONTACT_FORM_STEPS.ERROR_MESSAGE
			};
		} catch (error) {
			const errorConverted = error as Error;
			goToErrorMessage(errorConverted?.message);
			return {
				success: false,
				step: CONTACT_FORM_STEPS.ERROR_MESSAGE
			};
		}
	}

	return {
		goToContactFormStep,
		goToSuccessMessage,
		goToErrorMessage,
		goToOtpFormStep,
		sendForm,
		submitForm: handleFormSubmit,
		submitExternalForm,
		resendOtp,
		handleOtpFormValidation
	};
}
